import {useEffect, useRef, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router';
import {Box, Typography} from '@mui/material';
import {NoAuthLayout} from 'src/components/MainLayout/NoAuthLayout';
import {LoadingScreen} from 'src/components/LoadingScreen';
import {DtxSpacer} from 'src/components/common/DtxSpacer';
import {DefaultButton} from 'src/components/common/DefaultButton';
import {useAuth} from 'src/auth/context';
import * as idp from 'src/auth/idpClient';
import {takeFederatedLogin} from 'src/auth/federatedState';
import {IdpError, type FederatedProvider} from 'src/auth/types';
import {dashboardRoute, loginRoute} from 'src/routes';

/**
 * Where a federated sign-in comes back to.
 *
 * The IdP has already exchanged the upstream code and minted a short-lived
 * internal one; all that is left is to trade it for tokens. The provider is
 * whichever one this tab started with — the redirect itself does not say.
 */
export const AuthCallback = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const {completeLogin} = useAuth();

  const [error, setError] = useState<string | undefined>(undefined);

  // React mounts effects twice in development. The internal code is
  // single-use, so exchanging it twice would fail the second time and strand a
  // sign-in that actually worked.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const code = params.get('code');
    const state = params.get('state');

    if (!code || !state) {
      setError('That sign-in link is incomplete. Start again from the sign-in page.');
      return;
    }

    const pending = takeFederatedLogin(state);
    if (!pending) {
      setError('That sign-in did not start in this tab. Start again from the sign-in page.');
      return;
    }

    const exchange = async (provider: FederatedProvider) => {
      try {
        const tokens = await idp.exchangeFederatedCode(provider, state, code);
        await completeLogin(tokens);
        navigate(pending.returnTo ?? dashboardRoute, {replace: true});
      } catch (cause) {
        if (cause instanceof IdpError) {
          setError(cause.message);
          return;
        }
        console.error(cause);
        setError('Could not complete sign-in. Start again from the sign-in page.');
      }
    };

    void exchange(pending.provider);
  }, [params, completeLogin, navigate]);

  if (!error) return <LoadingScreen isFullScreen/>;

  return (
    <NoAuthLayout>
      <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
        <DtxSpacer space={2}/>
        <Typography role="alert">{error}</Typography>
        <DtxSpacer space={2}/>
        <DefaultButton onClick={() => navigate(loginRoute, {replace: true})}>
          Back to sign in
        </DefaultButton>
        <DtxSpacer space={2}/>
      </Box>
    </NoAuthLayout>
  );
};
